import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ExternalLink, Edit, Upload, Save, RefreshCw, Calendar } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { agendaService } from '@/services/agendaService';
import { getImageUrl } from '@/services/api';
import agenda071 from '/src/assets/agenda/saciteng.png';
import agenda081 from '/src/assets/agenda/saciteng2.png';

const AdminAgenda = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState({});
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formSettings, setFormSettings] = useState({
    event_title: '',
    event_subtitle: '',
    event_date: '',
    event_venue: '',
    event_location: '',
    day1_title: '',
    day2_title: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRef = React.useRef(null);
  const uploadDayRef = React.useRef(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [settingsData, imagesData] = await Promise.all([
        agendaService.getSettings(),
        agendaService.getImages(),
      ]);
      setSettings(settingsData || {});
      setImages(imagesData || []);
      setFormSettings({
        event_title: settingsData?.event_title || 'Agenda of SACIT Symposium 2025',
        event_subtitle: settingsData?.event_subtitle || 'SACIT Symposium 2025: Crafting Sustainability across ASEAN and Beyond',
        event_date: settingsData?.event_date || 'August 7 – 8, 2025',
        event_venue: settingsData?.event_venue || 'At the Sustainable Arts and Crafts Institute of Thailand (SACIT)',
        event_location: settingsData?.event_location || 'Bang Sai District, Phra Nakhon Si Ayutthaya Province, Thailand',
        day1_title: settingsData?.day1_title || 'Day 1 August 7, 2025',
        day2_title: settingsData?.day2_title || 'Day 2 August 8, 2025',
      });
    } catch (err) {
      console.error(err);
      toast({ title: 'เกิดข้อผิดพลาด', description: 'โหลดข้อมูลไม่ได้', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveHeader = async (e) => {
    e?.preventDefault();
    try {
      setSaving(true);
      await agendaService.updateSettings(formSettings);
      toast({ title: 'บันทึกสำเร็จ', description: 'อัปเดตหัวข้อแล้ว' });
      loadData();
    } catch (err) {
      toast({ title: 'เกิดข้อผิดพลาด', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = (day) => {
    uploadDayRef.current = day;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    const day = uploadDayRef.current;
    if (!file || !day) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'ไฟล์ไม่ถูกต้อง', description: 'กรุณาเลือกไฟล์รูปภาพ (JPG, PNG)', variant: 'destructive' });
      return;
    }
    try {
      setUploading(day);
      const path = await agendaService.uploadImage(file);
      const img = images.find((i) => i.day === day);
      if (img) {
        await agendaService.updateImage(img.id, path, img.image_alt);
        toast({ title: 'อัปเดตรูปสำเร็จ', description: `รูปกำหนดการวันที่ ${day} ถูกอัปเดตแล้ว` });
        loadData();
      }
    } catch (err) {
      toast({ title: 'เกิดข้อผิดพลาด', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  const getImageForDay = (day) => {
    const img = images.find((i) => i.day === day);
    if (img?.image_path?.startsWith('/uploads/')) return getImageUrl(img.image_path);
    return day === '1' ? agenda071 : agenda081;
  };

  const labelMap = {
    event_title: 'ชื่องาน',
    event_subtitle: 'หัวข้อย่อย',
    event_date: 'วันที่จัดงาน',
    event_venue: 'สถานที่',
    event_location: 'ที่อยู่สถานที่',
    day1_title: 'หัวข้อวันที่ 1',
    day2_title: 'หัวข้อวันที่ 2',
  };

  return (
    <>
      <Helmet>
        <title>จัดการกำหนดการ - ระบบจัดการ SACIT</title>
        <meta name="description" content="แก้ไขหัวข้อและรูปภาพกำหนดการ SACIT Symposium" />
      </Helmet>

      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-800">จัดการกำหนดการ</h1>
            <p className="text-gray-600 mt-1">แก้ไขหัวข้อและรูปภาพที่แสดงบนหน้าเว็บกำหนดการ</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData} disabled={loading} className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              รีเฟรช
            </Button>
            <Button variant="outline" asChild>
              <Link to="/agenda" target="_blank" className="inline-flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                ดูตัวอย่างหน้าเว็บ
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* หัวข้อและข้อมูลงาน */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-violet-600" />
            หัวข้อและข้อมูลงาน
          </h2>
          <form onSubmit={handleSaveHeader} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['event_title', 'event_subtitle', 'event_date', 'event_venue', 'event_location', 'day1_title', 'day2_title'].map((key) => (
                <div key={key} className={key === 'event_subtitle' || key === 'event_venue' || key === 'event_location' ? 'md:col-span-2' : ''}>
                  <Label htmlFor={key} className="text-gray-700">{labelMap[key]}</Label>
                  <Input
                    id={key}
                    value={formSettings[key] || ''}
                    onChange={(e) => setFormSettings((p) => ({ ...p, [key]: e.target.value }))}
                    className="mt-1"
                    placeholder={labelMap[key]}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                {saving ? 'กำลังบันทึก...' : 'บันทึกหัวข้อ'}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* รูปภาพกำหนดการ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white p-4 sm:p-6 rounded-xl shadow-lg border border-gray-200"
        >
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-violet-600" />
            รูปภาพกำหนดการ
          </h2>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
          {loading ? (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600 mx-auto" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <span className="font-medium text-gray-700">Day 1 - August 7, 2025</span>
                </div>
                <div className="p-4">
                  <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-4">
                    <img
                      src={getImageForDay('1')}
                      alt="Day 1 Agenda"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleImageSelect('1')}
                    disabled={uploading === '1'}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading === '1' ? 'กำลังอัปโหลด...' : 'เปลี่ยนรูป Day 1'}
                  </Button>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <span className="font-medium text-gray-700">Day 2 - August 8, 2025</span>
                </div>
                <div className="p-4">
                  <div className="aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden mb-4">
                    <img
                      src={getImageForDay('2')}
                      alt="Day 2 Agenda"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => handleImageSelect('2')}
                    disabled={uploading === '2'}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading === '2' ? 'กำลังอัปโหลด...' : 'เปลี่ยนรูป Day 2'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default AdminAgenda;
